import {
  AbstractBenefits,
  AbstractBenefitTier,
  AbstractBenefitPhase,
  AbstractBenefitPhaseEnum,
  PharmacyPreferencesAndFrequencies,
  PharmacyPreferences,
  PharmacyFrequencies,
} from "../../../../@types";
import { CONSTANTS } from "../../../../helpers";

export const costsByDrugTierTableId = "CostsByDrugTierTable";
export const costsByDrugTierTestIdPrefix = `${costsByDrugTierTableId}__row--`;

export interface Header {
  heading: string;
  footnoteIdx?: number;
}

export interface GeneralCost {
  brand: string;
  generic: string;
}

export interface CellData {
  isGeneric: boolean;
  cost?: string;
  generalCost?: GeneralCost;
  title?: string;
}

export interface Footnote {
  phaseName: string;
  footnote: string;
  colspan: number;
}

export interface AbstractBenefitsTableData {
  headers: Header[];
  rows: CellData[][];
  footnotes: Footnote[];
}

// Valid phases contain at least one instance of non-null cost data for a particular phase and tier combination
export const getValidPhases = (
  abstractBenefits: AbstractBenefits
): string[] => {
  return Object.keys(AbstractBenefitPhaseEnum).filter((phaseName: string) => {
    return (
      abstractBenefits[phaseName] &&
      abstractBenefits[phaseName].tiers &&
      abstractBenefits[phaseName].tiers.length > 0
    );
  });
};

export const getTierNameList = (
  abstractBenefits: AbstractBenefits
): string[] => {
  const validPhases = getValidPhases(abstractBenefits);

  // Find the phase with the most tiers. The table needs to accommodate this many rows
  const maxTiers = validPhases.reduce((largestNumberOfTiers, thisPhase) => {
    const phaseLength = abstractBenefits[thisPhase].tiers.length;
    return phaseLength > largestNumberOfTiers
      ? phaseLength
      : largestNumberOfTiers;
  }, 0);

  let maxTiersPhaseName: string | undefined;

  const validPhasesWithMaxTiers = validPhases.filter((phaseName: string) => {
    const phase = abstractBenefits[phaseName];

    if (phase && phase.tiers) {
      return phase.tiers.length === maxTiers;
    }
    return false;
  });

  // Find the name of the phase that has the most tiers
  if (validPhasesWithMaxTiers.length === 1) {
    maxTiersPhaseName = validPhasesWithMaxTiers[0];
  }

  // If there are more than one phase with max tiers, prioritize `initial_coverage`
  if (validPhasesWithMaxTiers.length > 1) {
    const initialCoveragePhase = validPhasesWithMaxTiers.find(
      (phaseName: string) =>
        phaseName === AbstractBenefitPhaseEnum.initial_coverage
    );
    maxTiersPhaseName = initialCoveragePhase || validPhasesWithMaxTiers[0];
  }

  // Return an array of tier labels for the phase with the most tiers
  if (maxTiersPhaseName) {
    const phaseWithMaxTiers: AbstractBenefitPhase | null =
      abstractBenefits[maxTiersPhaseName];

    if (phaseWithMaxTiers && phaseWithMaxTiers.tiers) {
      return (
        phaseWithMaxTiers.tiers
          // NB - All phases will have `tier_row_order` values for tiers, but only
          // initial coverage will have values that work for sorting (where the values
          // aren't all 0). This is how data is sent to us, and so sorting may not
          // work in some edge cases.
          .sort((tierA, tierB) => tierA.tier_row_order - tierB.tier_row_order)
          .map(tier => tier.label)
      );
    }
  }
  return [];
};

// check all tiers in the given phase for a non empty general cost value
// all other values are ignored for that phase and will not be displayed
export const phaseHasGeneralCost = (
  abstractBenefits: AbstractBenefits | null,
  phaseName: AbstractBenefitPhaseEnum
): boolean => {
  if (abstractBenefits) {
    const phase = abstractBenefits[phaseName];

    if (phase && phase.tiers) {
      return phase.tiers.some((tier: AbstractBenefitTier) => tier.general);
    }
  }
  return false;
};

export const phaseTranslations = {
  [AbstractBenefitPhaseEnum.initial_coverage]:
    "abstract_benefits.phase.initial_coverage",
  [AbstractBenefitPhaseEnum.coverage_gap]:
    "abstract_benefits.phase.coverage_gap",
  [AbstractBenefitPhaseEnum.catastrophic]:
    "abstract_benefits.phase.catastrophic",
  [AbstractBenefitPhaseEnum.lis]: "abstract_benefits.phase.lis",
  [AbstractBenefitPhaseEnum.platino]: "abstract_benefits.phase.platino",
};

export const dropdownTranslations = {
  [PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_std_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_std_freq_90_days",
  [PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_prfd_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_prfd_freq_90_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_std_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_std_freq_90_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_prfd_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_prfd_freq_90_days",
};

// Truthy if any cost value is found to not be null for the given pharmacy preference
const hasSomeCostForPharmacyPreference = (
  pharmacyPreference: PharmacyPreferences
) => (abstractBenefits: AbstractBenefits): boolean => {
  const keys = Object.keys(abstractBenefits);
  return keys.some((phase: string) => {
    if (abstractBenefits[phase] && abstractBenefits[phase].tiers) {
      return abstractBenefits[phase].tiers.some((tier: string) => {
        return tier[pharmacyPreference];
      });
    }
    return false;
  });
};

const hasPharmacyStandardRetail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.standard_retail
);

const hasPharmacyPreferredRetail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.preferred_retail
);

const hasPharmacyStandardMail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.standard_mail_order
);

const hasPharmacyPreferredMail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.preferred_mail_order
);

export const filterOptions = (
  abstractBenefits: AbstractBenefits
): PharmacyPreferencesAndFrequencies[] =>
  Object.keys(PharmacyPreferencesAndFrequencies).filter(
    (prefAndFreq: string) => {
      switch (prefAndFreq) {
        case PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_30_DAYS:
          return hasPharmacyStandardRetail(abstractBenefits);

        case PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_30_DAYS:
          return hasPharmacyPreferredRetail(abstractBenefits);

        case PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_90_DAYS:
          return hasPharmacyStandardMail(abstractBenefits);

        case PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_90_DAYS:
          return hasPharmacyPreferredMail(abstractBenefits);
        default:
          return false;
      }
    }
  ) as PharmacyPreferencesAndFrequencies[];

// Given a tier object, retrieve cost based on pharmacy preference and frequency
// If the cost for the selected frequency is empty, use the `days_any` cost
const getABCostValue = (
  tier: AbstractBenefitTier | null,
  pharmPref: PharmacyPreferences,
  freq: PharmacyFrequencies
): PharmacyFrequencies | string => {
  if (tier && tier[pharmPref]) {
    const pref = pharmPref.toString();

    if (tier && tier[pref] !== null) {
      const costToUse = tier[pref][freq];
      const daysAnyCost = tier[pref][PharmacyFrequencies.days_any];
      return costToUse || daysAnyCost;
    }
  }

  return "";
};

// given a phase object and tier index, retrieve cost based on pharmacy preference and frequency
export const getCostGivenPhaseTierPref = (
  phase: AbstractBenefitPhase,
  tierName: string,
  pharmPrefAndFreq: PharmacyPreferencesAndFrequencies
): string | undefined => {
  const currentTier = phase.tiers.find(tier => tier.label === tierName);

  if (currentTier) {
    let pharmacyPreference;
    let pharmacyFrequency;

    switch (pharmPrefAndFreq) {
      case PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_retail;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_retail;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_retail;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_retail;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      default:
        return "";
    }

    return getABCostValue(currentTier, pharmacyPreference, pharmacyFrequency);
  } else {
    return "";
  }
};

enum FootnoteEnum {
  footnote_1 = "The above cost-sharing only applies to some drugs on this tier. For all other drugs, you pay 25% for generic drugs and 25% for brand-name drugs.",
  footnote_2 = "For all other drugs, you pay 25% for generic drugs and 25% for brand-name drugs.",
  footnote_1_2019 = "The above cost-sharing only applies to some drugs on this tier. For all other drugs, you pay 37% for generic drugs and 25% for brand-name drugs.",
  footnote_2_2019 = "All drugs may not be offered with additional gap coverage, for all other drugs, you pay 44% for generic drugs and 35% for brand-name drugs.",
  footnote_3_2019 = "For all other drugs, you pay 37% for generic drugs and 25% for brand-name drugs.",
}

const translateFootnote = (footnote: string): string => {
  switch (footnote as FootnoteEnum) {
    case FootnoteEnum.footnote_1:
      return "abstract_benefits.footnote_1";
    case FootnoteEnum.footnote_2:
      return "abstract_benefits.footnote_2";
    case FootnoteEnum.footnote_1_2019:
      return "abstract_benefits.2019.footnote_1";
    case FootnoteEnum.footnote_2_2019:
      return "abstract_benefits.2019.footnote_2";
    case FootnoteEnum.footnote_3_2019:
      return "abstract_benefits.2019.footnote_3";
    default:
      return footnote;
  }
};

export const getGeneralCost = (phase: AbstractBenefitPhase): GeneralCost =>
  phase.tiers.reduce(
    (generalCost: GeneralCost, tier: AbstractBenefitTier) => {
      if (
        tier.label ===
        CONSTANTS.abstractBenefitTierNames.COST_SHARE_TIER_GENERIC_DRUGS
      ) {
        generalCost.generic = tier.general || "";
      } else if (
        tier.label ===
        CONSTANTS.abstractBenefitTierNames.COST_SHARE_TIER_BRAND_NAME_DRUGS
      ) {
        generalCost.brand = tier.general || "";
      }
      return generalCost;
    },
    { generic: "", brand: "" }
  );

export const scanFootnotes = (abstractBenefits: AbstractBenefits): Footnote[] =>
  getValidPhases(abstractBenefits).reduce(
    (footnotes: Footnote[], phaseName: string, _, phases) => {
      if (abstractBenefits) {
        const phase: AbstractBenefitPhase = abstractBenefits[phaseName];

        if (phase && phase.footnote) {
          return footnotes.concat([
            {
              phaseName,
              footnote: translateFootnote(phase.footnote),
              colspan: phases.length + 1,
            },
          ]);
        }
      }
      return footnotes;
    },
    []
  );

export const phaseHasFootnote = (
  footnotes: Footnote[],
  phaseName: string
): boolean => footnotes.some(footnote => footnote.phaseName === phaseName);

/**
 * Generate a data-testid attribute string with the correct order of the associated tier
 * @param num - The tier order value
 * @returns - The data-testid value
 */
export const getCostsByDrugTierRowTestId = (num: number | string): string =>
  `${costsByDrugTierTestIdPrefix}${num}`;

/**
 * Get the tier order from a data-testid string set on each table row
 * Return the correct tier order number as a string or the string "-1"
 */
export const getDrugTierOrderFromDataTestId = (testId: string): string => {
  const matches = /^.*(\d)$/.exec(testId);
  return matches?.length && matches[1] ? matches[1] : "-1";
};
